import type { I18n } from '@funkey/locales';
import type {
    ActivityAccessibilityFeature,
    ActivityGoal,
    ActivityTargetAudience,
} from '@funkey/constants';

/**
 * Translated labels for the sparse activity metadata enums (FUN-4807).
 *
 * Shared because the exact same wording has to appear on both sides of the
 * product: the partner/admin form that *tags* an activity
 * (`portal/general/ActivityGeneral.tsx`) and the public website filters that
 * *search* on those tags (`search/client/ActivityFilterDrawer.tsx`,
 * `search/client/activeFilterPills.ts`). Duplicating them would let the two
 * drift apart and double the POEditor keys.
 *
 * Only `_` is required, so both the full `I18n` and the pill helper's
 * `Pick<I18n, '_'>` satisfy it.
 */
type LabelI18n = Pick<I18n, '_'>;

export const ACTIVITY_GOAL_LABELS: Record<ActivityGoal, (i18n: LabelI18n) => string> = {
    COLLABORATION: i18n =>
        i18n._('activity.portal.goal.collaboration', undefined, { message: 'Collaboration' }),
    TEAM_COHESION: i18n =>
        i18n._('activity.portal.goal.teamCohesion', undefined, { message: 'Team cohesion' }),
    COMMUNICATION: i18n =>
        i18n._('activity.portal.goal.communication', undefined, { message: 'Communication' }),
    CONFLICT_RESOLUTION: i18n =>
        i18n._('activity.portal.goal.conflictResolution', undefined, {
            message: 'Conflict resolution',
        }),
    FUN_AND_RELAXATION: i18n =>
        i18n._('activity.portal.goal.funAndRelaxation', undefined, {
            message: 'Fun & relaxation',
        }),
    MENTAL_HEALTH: i18n =>
        i18n._('activity.portal.goal.mentalHealth', undefined, { message: 'Mental health' }),
    COMPANY_CULTURE: i18n =>
        i18n._('activity.portal.goal.companyCulture', undefined, { message: 'Company culture' }),
    LEADERSHIP: i18n =>
        i18n._('activity.portal.goal.leadership', undefined, { message: 'Leadership' }),
    CREATIVITY: i18n =>
        i18n._('activity.portal.goal.creativity', undefined, { message: 'Creativity' }),
    STRATEGY: i18n => i18n._('activity.portal.goal.strategy', undefined, { message: 'Strategy' }),
    ICEBREAKING: i18n =>
        i18n._('activity.portal.goal.icebreaking', undefined, { message: 'Icebreaking' }),
};

export const ACTIVITY_ACCESSIBILITY_LABELS: Record<
    ActivityAccessibilityFeature,
    (i18n: LabelI18n) => string
> = {
    WHEELCHAIR_ACCESSIBLE: i18n =>
        i18n._('activity.portal.accessibility.wheelchairAccessible', undefined, {
            message: 'Wheelchair accessible',
        }),
    ACCESSIBLE_PARKING: i18n =>
        i18n._('activity.portal.accessibility.accessibleParking', undefined, {
            message: 'Accessible parking',
        }),
    ACCESSIBLE_TOILETS: i18n =>
        i18n._('activity.portal.accessibility.accessibleToilets', undefined, {
            message: 'Accessible toilets',
        }),
    ELEVATOR_OR_RAMP: i18n =>
        i18n._('activity.portal.accessibility.elevatorOrRamp', undefined, {
            message: 'Elevator or ramp',
        }),
    MOBILITY_AID_AVAILABLE: i18n =>
        i18n._('activity.portal.accessibility.mobilityAidAvailable', undefined, {
            message: 'Mobility aid available',
        }),
    BLIND_FRIENDLY: i18n =>
        i18n._('activity.portal.accessibility.blindFriendly', undefined, {
            message: 'Suitable for blind and visually impaired people',
        }),
    DEAF_FRIENDLY: i18n =>
        i18n._('activity.portal.accessibility.deafFriendly', undefined, {
            message: 'Suitable for deaf and hard of hearing people',
        }),
    LOW_STIMULUS: i18n =>
        i18n._('activity.portal.accessibility.lowStimulus', undefined, {
            message: 'Low-stimulus environment',
        }),
    STRUCTURED_PROGRAM: i18n =>
        i18n._('activity.portal.accessibility.structuredProgram', undefined, {
            message: 'Clearly structured programme',
        }),
    FLEXIBLE_BREAKS: i18n =>
        i18n._('activity.portal.accessibility.flexibleBreaks', undefined, {
            message: 'Flexible breaks',
        }),
    ASSISTANCE_DOGS_WELCOME: i18n =>
        i18n._('activity.portal.accessibility.assistanceDogsWelcome', undefined, {
            message: 'Assistance dogs welcome',
        }),
    DIETARY_ACCOMMODATIONS: i18n =>
        i18n._('activity.portal.accessibility.dietaryAccommodations', undefined, {
            message: 'Dietary accommodations',
        }),
    SIMPLE_LANGUAGE: i18n =>
        i18n._('activity.portal.accessibility.simpleLanguage', undefined, {
            message: 'Simple language',
        }),
    EXTRA_TIME_ALLOWED: i18n =>
        i18n._('activity.portal.accessibility.extraTimeAllowed', undefined, {
            message: 'Extra time allowed',
        }),
};

/** The four accessibility buckets from `ACTIVITY_ACCESSIBILITY_FEATURES`, in display order. */
export const ACTIVITY_ACCESSIBILITY_GROUPS = [
    {
        title: (i18n: LabelI18n) =>
            i18n._('activity.portal.accessibility.group.physical', undefined, {
                message: 'Physical',
            }),
        features: [
            'WHEELCHAIR_ACCESSIBLE',
            'ACCESSIBLE_PARKING',
            'ACCESSIBLE_TOILETS',
            'ELEVATOR_OR_RAMP',
            'MOBILITY_AID_AVAILABLE',
        ],
    },
    {
        title: (i18n: LabelI18n) =>
            i18n._('activity.portal.accessibility.group.sensory', undefined, {
                message: 'Sensory',
            }),
        features: ['BLIND_FRIENDLY', 'DEAF_FRIENDLY', 'LOW_STIMULUS'],
    },
    {
        title: (i18n: LabelI18n) =>
            i18n._('activity.portal.accessibility.group.neurodiversity', undefined, {
                message: 'Neurodiversity',
            }),
        features: [
            'STRUCTURED_PROGRAM',
            'FLEXIBLE_BREAKS',
            'ASSISTANCE_DOGS_WELCOME',
            'DIETARY_ACCOMMODATIONS',
        ],
    },
    {
        title: (i18n: LabelI18n) =>
            i18n._('activity.portal.accessibility.group.communication', undefined, {
                message: 'Communication & cognitive',
            }),
        features: ['SIMPLE_LANGUAGE', 'EXTRA_TIME_ALLOWED'],
    },
] as const satisfies readonly {
    title: (i18n: LabelI18n) => string;
    features: readonly ActivityAccessibilityFeature[];
}[];

export const ACTIVITY_TARGET_AUDIENCE_LABELS: Record<
    ActivityTargetAudience,
    (i18n: LabelI18n) => string
> = {
    SENIOR_FRIENDLY: i18n =>
        i18n._('activity.portal.targetAudience.seniorFriendly', undefined, {
            message: 'Suitable for seniors',
        }),
    CHILD_FRIENDLY: i18n =>
        i18n._('activity.portal.targetAudience.childFriendly', undefined, {
            message: 'Suitable for children',
        }),
    STROLLER_FRIENDLY: i18n =>
        i18n._('activity.portal.targetAudience.strollerFriendly', undefined, {
            message: 'Stroller friendly',
        }),
};
